import type {
  SQLImportDatabase as UIAPISQLImportDatabase,
  SqlImportsDatabaseAttributes,
} from '@customerio/hydra-ui-api';

/**
 * A connected SQL-import database: the engine plus the connection settings the
 * workspace's imports read through. Backed by
 * `/v1/environments/{env}/sql_import_databases` (`services/ui_api/sql_imports/`).
 * There is no by-id read — a database reaches the cache through the list, or
 * through the import replies that sideload it (see `../sql-import/populate.ts`).
 * Create and update verify the connection server-side before saving, so a
 * rejection carries the connection error under `database`.
 */
export type SqlImportDatabase = UIAPISQLImportDatabase;

/** The database engines an import can read from. */
export type SqlDatabaseType = SqlImportsDatabaseAttributes['type'];

export const SqlDatabaseType = {
  mysql: 'mysql',
  postgres: 'postgres',
  snowflake: 'snowflake',
  bigQuery: 'bigquery',
  redshift: 'redshift',
  sqlserver: 'sqlserver',
} as const satisfies Record<string, SqlDatabaseType>;

/** Every database type, in declaration order. */
export const SQL_DATABASE_TYPES: readonly SqlDatabaseType[] = Object.values(SqlDatabaseType);

export const sqlImportDatabaseKeys = {
  all: (workspaceId: number) => ['workspace', workspaceId, 'sql_import_database'] as const,
  lists: (workspaceId: number) => [...sqlImportDatabaseKeys.all(workspaceId), 'list'] as const,
  list: (workspaceId: number, type: SqlDatabaseType | undefined) =>
    [...sqlImportDatabaseKeys.lists(workspaceId), type ?? 'all'] as const,
  details: (workspaceId: number) => [...sqlImportDatabaseKeys.all(workspaceId), 'detail'] as const,
  detail: (workspaceId: number, id: string) => [...sqlImportDatabaseKeys.details(workspaceId), id] as const,
};

/**
 * The name the backend gives the placeholder database a draft import starts
 * from. It is an implementation detail, not something a user typed, so it is
 * read as "unnamed" everywhere.
 */
const DRAFT_DATABASE_NAME = 'cio_draft';

/** Normalize a wire database for the cache: the draft placeholder has no name. */
export function parseSqlImportDatabase(raw: UIAPISQLImportDatabase): SqlImportDatabase {
  return raw.name === DRAFT_DATABASE_NAME ? { ...raw, name: '' } : raw;
}
